/**
 * Custom hook for managing Patient Profile Form state and logic
 *
 * Extracts form state management, validation, and event handling
 * from the PatientProfileForm component for better separation of concerns.
 *
 * @module hooks/usePatientProfileFormState
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { usePatientValidation } from '@/hooks/usePatientValidation';
import { scrollToFirstErrorField } from '@/utils/scrollToError';
import type {
  PatientProfileFormData,
  PatientPhoneData
} from '@/types/patientRegistration';
import { getLogger } from '@/services/shared/LoggerFactory';

// Define all form fields in order for validation and scrolling
const FORM_FIELDS = [
  'firstName',
  'lastName',
  'address.street',
  'address.city',
  'address.state',
  'address.postal_code',
  'phone',
  'date_of_birth',
] as const;

/**
 * Hook configuration interface
 */
export interface UsePatientProfileFormStateConfig {
  initialData?: Partial<PatientProfileFormData>;
  onSubmit: (data: PatientProfileFormData) => Promise<void>;
}

/**
 * Hook return interface
 */
export interface UsePatientProfileFormStateReturn {
  // Form state
  formData: PatientProfileFormData;
  fieldErrors: Record<string, string>;
  touchedFields: Set<string>;
  fieldRefs: React.RefObject<Record<string, HTMLInputElement | HTMLElement | null>>;

  // Form fields configuration
  FORM_FIELDS: readonly string[];

  // Utility functions
  getFieldValue: (fieldName: string) => unknown;
  validateField: (fieldName: string, value?: unknown) => boolean;

  // Event handlers
  handleFieldChange: (fieldName: string, value: unknown) => void;
  handleFieldBlur: (fieldName: string) => void;
  handlePhoneChange: (phoneData: { countryCode: string; isoCode: string; number: string }) => void;
  handlePhotoChange: (file: File | undefined) => void;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
}

/**
 * Custom hook for managing patient profile form state and logic
 */
export const usePatientProfileFormState = ({
  initialData,
  onSubmit,
}: UsePatientProfileFormStateConfig): UsePatientProfileFormStateReturn => {
  const logger = getLogger();
  const validation = usePatientValidation();

  // Field refs for scroll-to-error functionality
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLElement | null>>({});

  // Form state
  const [formData, setFormData] = useState<PatientProfileFormData>({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    address: {
      street: initialData?.address?.street || '',
      city: initialData?.address?.city || '',
      state: initialData?.address?.state || '',
      postal_code: initialData?.address?.postal_code || '',
      country: initialData?.address?.country || 'AU'
    },
    phone: initialData?.phone || {
      number: '',
      country_code: '61',
      iso_code: 'AU'
    },
    date_of_birth: initialData?.date_of_birth || '',
    photo: initialData?.photo
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        address: initialData.address || prev.address,
        phone: initialData.phone || prev.phone,
      }));
    }
  }, [initialData]);

  // Helper function to get field value
  const getFieldValue = useCallback((fieldName: string) => {
    if (fieldName.startsWith('address.')) {
      const addressField = fieldName.split('.')[1] as keyof typeof formData.address;
      return formData.address[addressField];
    }
    return formData[fieldName as keyof PatientProfileFormData];
  }, [formData]);

  // Validation functions
  const validateField = useCallback((fieldName: string, value?: unknown) => {
    const fieldValue = value ?? getFieldValue(fieldName);

    const validationMap: Record<string, () => { isValid: boolean; error?: string }> = {
      'firstName': () => validation.validateName(fieldValue as string, 'First name'),
      'lastName': () => validation.validateName(fieldValue as string, 'Last name'),
      'address.street': () => validation.validateStreet(fieldValue as string),
      'address.city': () => validation.validateCity(fieldValue as string),
      'address.state': () => validation.validateState(fieldValue as string),
      'address.postal_code': () => validation.validatePostalCode(fieldValue as string),
      'phone': () => validation.validatePhone(fieldValue as PatientPhoneData),
      'date_of_birth': () => validation.validateDateOfBirth(fieldValue as string),
      'photo': () => {
        if (fieldValue) {
          return validation.validatePhoto(fieldValue as File);
        } else {
          return { isValid: true }; // Photo is optional
        }
      },
    };

    const result = validationMap[fieldName]?.() || { isValid: true };

    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: result.isValid ? '' : (result.error || ''),
    }));

    return result.isValid;
  }, [validation, getFieldValue]);

  // Event handlers
  const handleFieldChange = useCallback((fieldName: string, value: unknown) => {
    if (fieldName.startsWith('address.')) {
      const addressField = fieldName.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value,
      }));
    }

    // Validate if field has been touched
    if (touchedFields.has(fieldName)) {
      validateField(fieldName, value);
    }
  }, [touchedFields, validateField]);

  const handleFieldBlur = useCallback((fieldName: string) => {
    setTouchedFields(prev => new Set([...prev, fieldName]));

    let value;
    if (fieldName.startsWith('address.')) {
      const addressField = fieldName.split('.')[1];
      value = formData.address[addressField as keyof typeof formData.address];
    } else {
      value = formData[fieldName as keyof PatientProfileFormData];
    }

    validateField(fieldName, value);
  }, [formData, validateField]);

  // Specific handlers for complex fields
  const handlePhoneChange = useCallback((phoneData: { countryCode: string; isoCode: string; number: string }) => {
    // Convert from CountryPhoneSelector format to PatientPhoneData format
    const patientPhoneData: PatientPhoneData = {
      number: phoneData.number,
      country_code: phoneData.countryCode.replace('+', ''),
      iso_code: phoneData.isoCode
    };
    handleFieldChange('phone', patientPhoneData);
  }, [handleFieldChange]);

  const handlePhotoChange = useCallback((file: File | undefined) => {
    handleFieldChange('photo', file);
  }, [handleFieldChange]);

  // Form submission
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    // Mark all fields as touched
    const allFields = [
      'firstName', 'lastName',
      'address.street', 'address.city', 'address.state', 'address.postal_code',
      'phone', 'date_of_birth'
    ];
    setTouchedFields(new Set(allFields));

    // Validate all fields and collect results with errors
    const validationResults: { field: string; isValid: boolean; error?: string }[] = [];
    const newFieldErrors: Record<string, string> = {};

    allFields.forEach(field => {
      let value;
      if (field.startsWith('address.')) {
        const addressField = field.split('.')[1];
        value = formData.address[addressField as keyof typeof formData.address];
      } else {
        value = formData[field as keyof PatientProfileFormData];
      }

      let result;
      switch (field) {
        case 'firstName':
          result = validation.validateName(value as string, 'First name');
          break;
        case 'lastName':
          result = validation.validateName(value as string, 'Last name');
          break;
        case 'address.street':
          result = validation.validateStreet(value as string);
          break;
        case 'address.city':
          result = validation.validateCity(value as string);
          break;
        case 'address.state':
          result = validation.validateState(value as string);
          break;
        case 'address.postal_code':
          result = validation.validatePostalCode(value as string);
          break;
        case 'phone':
          result = validation.validatePhone(value as PatientPhoneData);
          break;
        case 'date_of_birth':
          result = validation.validateDateOfBirth(value as string);
          break;
        default:
          result = { isValid: true };
      }

      validationResults.push({ field, ...result });
      newFieldErrors[field] = result.isValid ? '' : (result.error || '');
    });

    // Also validate photo if provided
    if (formData.photo) {
      const photoResult = validation.validatePhoto(formData.photo);
      validationResults.push({ field: 'photo', ...photoResult });
      newFieldErrors.photo = photoResult.isValid ? '' : (photoResult.error || '');
    }

    // Update field errors state
    setFieldErrors(newFieldErrors);

    const isFormValid = validationResults.every(result => result.isValid);

    if (!isFormValid) {
      // Create immediate validation state for scroll-to-error
      const immediateValidationState: Record<string, { touched?: boolean; result: { isValid: boolean; error?: string } }> = {};

      FORM_FIELDS.forEach(field => {
        const validationResult = validationResults.find(r => r.field === field);
        immediateValidationState[field] = {
          touched: true,
          result: {
            isValid: validationResult?.isValid ?? true,
            error: validationResult?.error
          }
        };
      });

      // Use immediate validation state for scrolling
      setTimeout(() => {
        scrollToFirstErrorField(
          [...FORM_FIELDS],
          immediateValidationState,
          fieldRefs.current,
          {
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
            focusDelay: 300,
            shouldFocus: true
          }
        );
      }, 100);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handling is managed by parent component
      logger.error('Profile form submission error:', error);
    }
  }, [formData, validation, onSubmit]);

  return {
    // Form state
    formData,
    fieldErrors,
    touchedFields,
    fieldRefs,

    // Form fields configuration
    FORM_FIELDS,

    // Utility functions
    getFieldValue,
    validateField,

    // Event handlers
    handleFieldChange,
    handleFieldBlur,
    handlePhoneChange,
    handlePhotoChange,
    handleSubmit,
  };
};